// Season leaderboard (admin season page → Leaderboard tab, and the public /season/:id page).
// GET /api/seasons/:id/board (admin, live matches count) or /api/seasons/public/:id/board
// (finished matches only): players + team standings, whole season / one match / one map, filtered
// by stage or map. Players: podium + sortable table (cards on phones), a row opens the breakdown.
// Teams: standings with bracket placements, a row opens the team card (CT/T, pistols, map record).
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import TeamLogo from '../teams/TeamLogo';
import { COLS, FLUX3_COLS, cellValue, ColHeader } from '../ext/ExtPlayerStats';
import { TeamCard } from '../ext/ExtTournamentStats';
import { RatingLabel, ratingColor, ratingOf } from '../ext/extUi';
import './seasonBoard.css';

const publicApi = axios.create(); // the public page never sends a token
delete publicApi.defaults.headers.common.Authorization;

const mapLabel = (n) => String(n || '?').replace(/^(de|cs)_/, '');
const signed = (v) => (v > 0 ? `+${v}` : `${v}`);
const diffClass = (v) => (v > 0 ? 'pos' : v < 0 ? 'neg' : '');
const logoTeam = (tm) => tm && ({ id: tm.team_id, name: tm.name, logo_url: tm.logo_url, avatar_v: tm.avatar_v });
const pct = (x) => (x && x.win_pct != null ? `${Math.round(x.win_pct)}%` : '—');

// sortable value of a player column (same rules as the ext stats table)
const colValue = (c, p) => {
  if (c.key === 'entry') return p.entry_kills - p.opening_deaths;
  if (c.key === 'mk') return p.multikills['3k'] + p.multikills['4k'] * 2 + p.multikills['5k'] * 3;
  if (c.key === 'bomb') return p.bomb_plants + p.bomb_defuses;
  const v = cellValue(c, p);
  return typeof v === 'number' ? v : Number(v) || 0;
};

function Place({ placement }) {
  const { t } = useTranslation();
  if (!placement) return null;
  const medal = { 1: '🥇', 2: '🥈', 3: '🥉' }[placement.place];
  const text = placement.shared ? '3-4' : placement.place === 4 ? '4th' : null;
  const title = placement.place === 1 ? t('seasonBoard.champion', 'Champion') : placement.place === 2 ? t('seasonBoard.runnerUp', 'Runner-up')
    : placement.shared ? t('seasonBoard.semifinalist', 'Semi-finalist') : t('seasonBoard.placeN', '{{n}}. place', { n: placement.place });
  return <span className={`sb-place p${placement.place}`} title={title}>{medal || ''}{text && <b>{text}</b>}</span>;
}

/** A player's numbers beyond the table: multi-kills, clutches, entries, utility, bomb. */
function PlayerDetail({ p }) {
  const { t } = useTranslation();
  const items = [
    [t('seasonBoard.d.maps', 'Maps / matches'), `${p.maps_played ?? '—'} / ${p.matches_played}`],
    [t('seasonBoard.d.rounds', 'Rounds'), p.rounds],
    [t('seasonBoard.d.damage', 'Damage'), p.damage],
    [t('seasonBoard.d.entries', 'First kills – deaths'), `${p.entry_kills} – ${p.opening_deaths}`],
    [t('seasonBoard.d.trades', 'Trade kills / traded deaths'), `${p.trade_kills} / ${p.traded_deaths}`],
    ['2K / 3K / 4K / 5K', `${p.multikills['2k']} / ${p.multikills['3k']} / ${p.multikills['4k']} / ${p.multikills['5k']}`],
    [t('seasonBoard.d.clutches', 'Clutches 1v1–1v5'), `${p.clutches['1v1']} / ${p.clutches['1v2']} / ${p.clutches['1v3']} / ${p.clutches['1v4']} / ${p.clutches['1v5']}`],
    [t('seasonBoard.d.flash', 'Flash assists / enemies flashed'), `${p.flash_assists} / ${p.enemies_flashed}`],
    [t('seasonBoard.d.util', 'Utility damage'), p.utility_damage],
    [t('seasonBoard.d.bomb', 'Plants / defuses'), `${p.bomb_plants} / ${p.bomb_defuses}`],
    [t('seasonBoard.d.special', 'Wallbang / no-scope / smoke / blind kills'), `${p.special_kills.wallbang} / ${p.special_kills.noscope} / ${p.special_kills.through_smoke} / ${p.special_kills.blind}`],
    ['MVP', p.mvps],
  ];
  return (
    <div className="sb-detail">
      {items.map(([k, v]) => <div key={k}><span>{k}</span><b>{v}</b></div>)}
    </div>
  );
}

function Podium({ players }) {
  const { t } = useTranslation();
  if (players.length < 3) return null;
  return (
    <div className="sb-podium">
      {players.slice(0, 3).map((p, i) => (
        <div key={p.steam_id} className={`sb-pod r${i + 1}`}>
          <div className="sb-pod-rank">{i + 1}</div>
          <div className="sb-pod-who">
            <TeamLogo team={logoTeam(p.team)} size={34} radius={8} />
            <div>
              <div className="sb-pod-name">{p.name}</div>
              <div className="sb-pod-team">{p.team ? p.team.name : '—'}</div>
            </div>
          </div>
          <div className="sb-pod-rating" style={{ color: ratingColor(ratingOf(p)) }}>{ratingOf(p).toFixed(2)}</div>
          <div className="sb-pod-line">
            <span>K/D <b>{p.kd}</b></span><span>ADR <b>{p.adr}</b></span><span>KAST <b>{p.kast_pct}%</b></span>
            <span>{t('seasonBoard.mapsN', '{{n}} maps', { n: p.maps_played ?? p.matches_played })}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

function PlayersView({ players }) {
  const { t } = useTranslation();
  const [sort, setSort] = useState({ key: 'rating', desc: true });
  const [open, setOpen] = useState(null);
  // Flux 3.0 (HLTV 3.0-style) + Round Swing, next to HLTV 2.0
  const cols = useMemo(() => [{ key: 'maps_played', label: t('seasonBoard.colMaps', 'Maps'), title: 'Maps played' }, { key: 'rounds', label: 'Rds', title: 'Rounds played' }, ...COLS, ...FLUX3_COLS], [t]);
  const rows = useMemo(() => {
    const c = cols.find(x => x.key === sort.key);
    return [...players].sort((a, b) => {
      if (sort.key === 'name') return sort.desc ? b.name.localeCompare(a.name) : a.name.localeCompare(b.name);
      const d = colValue(c, a) - colValue(c, b);
      return (sort.desc ? -d : d) || (ratingOf(b) - ratingOf(a));
    });
  }, [players, sort, cols]);
  const click = (key) => setSort(s => ({ key, desc: s.key === key ? !s.desc : key !== 'name' }));
  const arrow = (key) => (sort.key === key ? (sort.desc ? ' ↓' : ' ↑') : '');
  if (!players.length) return <div className="sb-empty">{t('seasonBoard.noPlayers', 'No player stats for this selection yet.')}</div>;

  return (
    <>
      {/* wide screens: the full table */}
      <div className="sb-table-wrap">
        <table className="sb-table">
          <thead>
            <tr>
              <th className="l sticky0">#</th>
              <th className="l sticky1" onClick={() => click('name')}>{t('seasonBoard.player', 'Player')}{arrow('name')}</th>
              {cols.map(c => <ColHeader key={c.key} c={c} suffix={arrow(c.key)} onClick={() => click(c.key)} />)}
            </tr>
          </thead>
          <tbody>
            {rows.map((p, i) => (
              <React.Fragment key={p.steam_id}>
                <tr className={`row${open === p.steam_id ? ' open' : ''}`} onClick={() => setOpen(o => (o === p.steam_id ? null : p.steam_id))}>
                  <td className="l sticky0 muted">{i + 1}</td>
                  <td className="l sticky1 who">
                    <TeamLogo team={logoTeam(p.team)} size={20} radius={5} />
                    <span className="nm">{p.name}</span>
                    {p.team && <span className="tm">{p.team.tag || p.team.name}</span>}
                  </td>
                  {cols.map(c => {
                    const v = cellValue(c, p);
                    const color = c.color ? c.color(v) : undefined;
                    return <td key={c.key} className={c.key === 'rating' || c.key === 'kills' ? 'b' : ''} style={color ? { color } : undefined}>{c.fmt ? c.fmt(v) : v}</td>;
                  })}
                </tr>
                {open === p.steam_id && <tr className="detail"><td colSpan={cols.length + 2}><PlayerDetail p={p} /></td></tr>}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* phones: one card per player */}
      <div className="sb-cards">
        <div className="sb-sortbar">
          {[['rating', null], ['kills', 'K'], ['adr', 'ADR'], ['kast_pct', 'KAST'], ['kd_diff', '+/-']].map(([k, l]) => (
            <button key={k} type="button" className={sort.key === k ? 'on' : ''} onClick={() => click(k)}>{l || <RatingLabel />}{arrow(k)}</button>
          ))}
        </div>
        {rows.map((p, i) => (
          <div key={p.steam_id} className="sb-card" onClick={() => setOpen(o => (o === p.steam_id ? null : p.steam_id))}>
            <div className="sb-card-top">
              <span className="muted">{i + 1}</span>
              <TeamLogo team={logoTeam(p.team)} size={26} radius={6} />
              <div className="who"><div className="nm">{p.name}</div><div className="tm">{p.team ? p.team.name : '—'}</div></div>
              <div className="rt" style={{ color: ratingColor(ratingOf(p)) }}>{ratingOf(p).toFixed(2)}</div>
            </div>
            <div className="sb-card-grid">
              <span>K <b>{p.kills}</b></span><span>D <b>{p.deaths}</b></span><span>A <b>{p.assists}</b></span><span>+/- <b className={diffClass(p.kd_diff)}>{signed(p.kd_diff)}</b></span>
              <span>ADR <b>{p.adr}</b></span><span>KAST <b>{p.kast_pct}%</b></span><span>HS <b>{p.hs_pct}%</b></span><span>{t('seasonBoard.colMaps', 'Maps')} <b>{p.maps_played}</b></span>
            </div>
            {open === p.steam_id && <PlayerDetail p={p} />}
          </div>
        ))}
      </div>
    </>
  );
}

const TEAM_COLS = [
  { key: 'matches', label: 'W-L-D', get: tm => tm.matches.won * 1000 - tm.matches.lost, show: tm => `${tm.matches.won}-${tm.matches.lost}${tm.matches.draw ? `-${tm.matches.draw}` : ''}`, onlyWhole: true },
  { key: 'maps', label: 'Maps', get: tm => tm.maps.won - tm.maps.lost, show: tm => `${tm.maps.won}-${tm.maps.lost}` },
  { key: 'rounds', label: 'Rounds', get: tm => tm.rounds.won, show: tm => `${tm.rounds.won}-${tm.rounds.lost}` },
  { key: 'rd', label: '+/-', get: tm => tm.rounds.diff, show: tm => signed(tm.rounds.diff), cls: tm => diffClass(tm.rounds.diff) },
  { key: 'ct', label: 'CT', get: tm => tm.ct.win_pct ?? -1, show: tm => pct(tm.ct) },
  { key: 't', label: 'T', get: tm => tm.t.win_pct ?? -1, show: tm => pct(tm.t) },
  { key: 'pistol', label: 'Pistol', get: tm => tm.pistol.win_pct ?? -1, show: tm => pct(tm.pistol) },
];

function TeamsView({ teams, wholeMatch }) {
  const { t } = useTranslation();
  const [sort, setSort] = useState({ key: 'standing', desc: false });
  const [open, setOpen] = useState(null);
  const cols = TEAM_COLS.filter(c => wholeMatch || !c.onlyWhole);
  const ratingKey = 'avg_hltv_rating';
  const rows = useMemo(() => {
    const ranked = teams.map((tm, i) => ({ tm, standing: i }));
    if (sort.key === 'standing') return sort.desc ? ranked.reverse() : ranked;
    const val = (x) => (sort.key === 'rating' ? x.tm.totals[ratingKey] : sort.key === 'place' ? -(x.tm.placement ? x.tm.placement.place : 99) : TEAM_COLS.find(c => c.key === sort.key).get(x.tm));
    return [...ranked].sort((a, b) => (sort.desc ? val(b) - val(a) : val(a) - val(b)) || a.standing - b.standing);
  }, [teams, sort, ratingKey]);
  const click = (key) => setSort(s => ({ key, desc: s.key === key ? !s.desc : key !== 'standing' }));
  const arrow = (key) => (sort.key === key ? (sort.desc ? ' ↓' : ' ↑') : '');
  if (!teams.length) return <div className="sb-empty">{t('seasonBoard.noTeams', 'No team results for this selection yet.')}</div>;
  const anyPlace = teams.some(tm => tm.placement);

  return (
    <div className="sb-table-wrap teams">
      <table className="sb-table">
        <thead>
          <tr>
            <th className="l" onClick={() => click('standing')}>#{arrow('standing')}</th>
            <th className="l">{t('seasonBoard.team', 'Team')}</th>
            {anyPlace && <th onClick={() => click('place')} title={t('seasonBoard.placeHint', 'Final placement in the season’s bracket')}>{t('seasonBoard.place', 'Place')}{arrow('place')}</th>}
            {cols.map(c => <th key={c.key} onClick={() => click(c.key)} title={t(`seasonBoard.tcol.${c.key}`, c.label)}>{t(`seasonBoard.tcolShort.${c.key}`, c.label)}{arrow(c.key)}</th>)}
            <th onClick={() => click('rating')} title={t('seasonBoard.avgRatingHint', 'Average HLTV 2.0 rating of the lineup, weighted by rounds')}>
              <RatingLabel />{arrow('rating')}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ tm, standing }) => {
            const r = Number(tm.totals[ratingKey]) || 0;
            return (
              <React.Fragment key={tm.key}>
                <tr className={`row${open === tm.key ? ' open' : ''}`} onClick={() => setOpen(o => (o === tm.key ? null : tm.key))}>
                  <td className="l muted">{standing + 1}</td>
                  <td className="l who">
                    <TeamLogo team={logoTeam(tm)} size={22} radius={6} />
                    <span className="nm">{tm.name}</span>
                    {tm.tag && <span className="tm">{tm.tag}</span>}
                  </td>
                  {anyPlace && <td><Place placement={tm.placement} /></td>}
                  {cols.map(c => <td key={c.key} className={c.cls ? c.cls(tm) : ''}>{c.show(tm)}</td>)}
                  <td className="b" style={{ color: ratingColor(r) }}>{r.toFixed(2)}</td>
                </tr>
                {open === tm.key && (
                  <tr className="detail"><td colSpan={cols.length + 3 + (anyPlace ? 1 : 0)}>
                    <TeamCard team={tm} rank={standing + 1} wholeMatch={wholeMatch} />
                  </td></tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default function SeasonBoard({ seasonId, publicView = false, onLoad }) {
  const { t } = useTranslation();
  const [view, setView] = useState('players');
  const [stages, setStages] = useState([]);
  const [map, setMap] = useState('');
  const [matchId, setMatchId] = useState('');
  const [mapIndex, setMapIndex] = useState('all');
  const [team, setTeam] = useState('');
  const [minMaps, setMinMaps] = useState(0);
  const [q, setQ] = useState('');
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (silent) => {
    if (!silent) setBusy(true);
    try {
      const params = {};
      if (matchId) { params.match_id = matchId; if (mapIndex !== 'all') params.map_index = mapIndex; }
      else { if (stages.length) params.stages = stages.join(','); if (map) params.map = map; }
      const r = publicView ? await publicApi.get(`/api/seasons/public/${seasonId}/board`, { params }) : await axios.get(`/api/seasons/${seasonId}/board`, { params });
      setData(r.data); setError('');
      if (onLoad) onLoad(r.data);
    } catch (e) {
      setError((e.response && e.response.data && e.response.data.error) || t('seasonBoard.loadFailed', 'Could not load the leaderboard'));
    } finally { setBusy(false); }
  }, [seasonId, publicView, matchId, mapIndex, stages, map, t]); // eslint-disable-line react-hooks/exhaustive-deps -- onLoad is a fresh arrow each render
  useEffect(() => { load(false); }, [load]);
  // the admin board counts live matches: keep it fresh while the tab is visible
  useEffect(() => {
    if (publicView) return undefined;
    const iv = setInterval(() => { if (!document.hidden) load(true); }, 20000);
    return () => clearInterval(iv);
  }, [publicView, load]);

  const players = useMemo(() => {
    if (!data) return [];
    const s = q.trim().toLowerCase();
    return data.players.filter(p => (!team || (p.team && p.team.key === team)) && (p.maps_played || 0) >= minMaps
      && (!s || p.name.toLowerCase().includes(s) || String(p.steam_id).includes(s)));
  }, [data, team, minMaps, q]);
  const podium = useMemo(() => [...players].sort((a, b) => ratingOf(b) - ratingOf(a)), [players]);

  if (!data && !error) return <div className="sb-empty">{t('leaderboard.loading', 'Loading leaderboard...')}</div>;
  if (!data) return <div className="sb-empty err">{error}</div>;

  const filters = data.filters || { stages: [], maps: [] };
  const scopeMaps = (data.scope && data.scope.maps) || [];
  const wholeMatch = !(matchId && mapIndex !== 'all') && !map;
  const toggleStage = (k) => setStages(s => (s.includes(k) ? s.filter(x => x !== k) : [...s, k]));
  const teamsForFilter = [...data.teams].sort((a, b) => String(a.name).localeCompare(String(b.name)));
  const filtered = stages.length || map || matchId || team || minMaps || q;

  return (
    <div className={`sb${busy ? ' busy' : ''}`}>
      <div className="sb-bar">
        <div className="sb-seg" role="tablist">
          <button type="button" role="tab" aria-selected={view === 'players'} className={view === 'players' ? 'on' : ''} onClick={() => setView('players')}>
            {t('seasonBoard.players', 'Players')} <span>{data.players.length}</span>
          </button>
          <button type="button" role="tab" aria-selected={view === 'teams'} className={view === 'teams' ? 'on' : ''} onClick={() => setView('teams')}>
            {t('seasonBoard.teams', 'Teams')} <span>{data.teams.length}</span>
          </button>
        </div>
        <div className="sb-spacer" />
        <span className="sb-note">
          {data.live ? t('seasonBoard.basedLive', '{{n}} matches · live matches included', { n: data.matches_counted })
            : t('seasonBoard.based', 'Based on {{n}} finished matches', { n: data.matches_counted })}
        </span>
        <span className="sb-rating" title={t('ext.stat.ratingHltv', 'HLTV Rating 2.0 (estimate, same formula on every match)')}><RatingLabel /></span>
      </div>

      <div className="sb-filters">
        <select value={matchId} onChange={e => { setMatchId(e.target.value); setMapIndex('all'); }} aria-label={t('seasonBoard.scope', 'Scope')}>
          <option value="">{t('seasonBoard.wholeSeason', 'Whole season')}</option>
          {data.matches.map(m => <option key={m.id} value={m.id}>{`#${m.id} · ${m.team1_name || '?'} vs ${m.team2_name || '?'}${m.best_of > 1 ? ` · BO${m.best_of}` : ''}${m.status === 'live' ? ' · LIVE' : ''}`}</option>)}
        </select>
        {matchId ? (
          <div className="sb-chips">
            <button type="button" className={mapIndex === 'all' ? 'on' : ''} onClick={() => setMapIndex('all')}>{t('seasonBoard.allMaps', 'All maps')}</button>
            {scopeMaps.map(m => (
              <button key={m.index} type="button" className={String(mapIndex) === String(m.index) ? 'on' : ''} onClick={() => setMapIndex(m.index)}>
                {mapLabel(m.name)}{m.score ? ` ${m.score.team1}-${m.score.team2}` : ''}
              </button>
            ))}
          </div>
        ) : (
          <>
            {filters.maps.length > 1 && (
              <select value={map} onChange={e => setMap(e.target.value)} aria-label={t('seasonBoard.map', 'Map')}>
                <option value="">{t('seasonBoard.allMaps', 'All maps')}</option>
                {filters.maps.map(m => <option key={m.map} value={m.map}>{`${mapLabel(m.map)} (${m.count})`}</option>)}
              </select>
            )}
            {filters.stages.length > 1 && (
              <div className="sb-chips">
                {filters.stages.map(s => (
                  <button key={s.key} type="button" className={stages.includes(s.key) ? 'on' : ''} onClick={() => toggleStage(s.key)}>
                    {t(`seasonBoard.stage.${s.key}`, s.label)} <span>{s.count}</span>
                  </button>
                ))}
              </div>
            )}
          </>
        )}
        {view === 'players' && (
          <>
            <select value={team} onChange={e => setTeam(e.target.value)} aria-label={t('seasonBoard.team', 'Team')}>
              <option value="">{t('seasonBoard.allTeams', 'All teams')}</option>
              {teamsForFilter.map(tm => <option key={tm.key} value={tm.key}>{tm.name}</option>)}
            </select>
            <select value={minMaps} onChange={e => setMinMaps(Number(e.target.value))} aria-label={t('seasonBoard.minMaps', 'Minimum maps')}>
              {[0, 1, 2, 3, 5, 8, 10].map(n => <option key={n} value={n}>{n ? t('seasonBoard.minMapsN', '{{n}}+ maps', { n }) : t('seasonBoard.anyMaps', 'Any maps')}</option>)}
            </select>
            <input className="sb-search" value={q} onChange={e => setQ(e.target.value)} placeholder={t('seasonBoard.search', 'Search player…')} />
          </>
        )}
        {filtered && <button type="button" className="sb-clear" onClick={() => { setStages([]); setMap(''); setMatchId(''); setMapIndex('all'); setTeam(''); setMinMaps(0); setQ(''); }}>{t('seasonBoard.clear', 'Clear filters')}</button>}
      </div>

      {view === 'players' ? (
        <>
          {!q && <Podium players={podium} />}
          <PlayersView players={players} />
          <div className="sb-legend">{t('seasonBoard.legend', 'Click a player for the full breakdown. FK-FD first kills – first deaths · TK trade kills · 1vX clutches won · FA flash assists · EF enemies flashed · UD utility damage · P/D bomb plants / defuses')}</div>
        </>
      ) : (
        <>
          <TeamsView teams={data.teams} wholeMatch={wholeMatch} />
          <div className="sb-legend">{t('seasonBoard.teamsLegend', 'Standings: matches won, then fewest lost, then map difference, then round difference. Click a team for CT / T sides, pistol rounds, rounds won by and its record per map.')}</div>
        </>
      )}
    </div>
  );
}
